import { buttonForKey, LONG_PRESS_MS, type ButtonDefault, type RemoteButton } from "./remoteMap.js";
import { useRemoteMap } from "./remoteMapStore.js";
import { runRemoteAction } from "./remoteActions.js";
import { isFeatureUnlocked } from "../library/proFeatures.js";

/**
 * The one place a remote button is turned into its action.
 *
 * A capture listener on the window, so it hears a button before any screen's
 * own handler. For a button left on a BUILT-IN default it does nothing at all:
 * the key goes on exactly as before this existed. It steps in only for a
 * button the viewer changed, or one whose default is an action run here (the
 * buttons that used to do nothing).
 *
 * LONG PRESS. A button with a long-press action waits for the key to come up
 * (a press) or to be held LONG_PRESS_MS (a long press, which fires at once,
 * while still held — the viewer sees it happen). A press whose action is the
 * built-in one is handed back to the app as the same key, marked so it passes
 * here untouched. A button with no long-press action acts on the key going
 * down, with no wait.
 */

const REPEATS = new Set(["channelNext", "channelPrev", "seekBack", "seekForward"]);

/** Keys this module re-sent itself, to pass straight through. */
const replayed = new WeakSet<Event>();

/** What a button does right now: the viewer's choice, else its default. */
export function effectiveAction(button: RemoteButton, kind: "press" | "longPress"): ButtonDefault | null {
  // A free viewer's own button choices are not applied (Pro); what they saved is kept.
  const chosen = isFeatureUnlocked("customRemote") ? useRemoteMap.getState().overrides[button.id]?.[kind] : undefined;
  if (chosen) return { action: chosen, builtIn: false };
  return kind === "press" ? button.press : button.longPress;
}

/**
 * Watching: the picture full screen, nothing focused and no popup open —
 * the one place the arrows and OK can be changed.
 */
export function isWatching(): boolean {
  if (!document.querySelector(".player-surface--fullscreen")) return false;
  const active = document.activeElement;
  if (active && active !== document.body && active !== document.documentElement) return false;
  return !document.querySelector('[role="dialog"], [role="menu"], [data-tvkeyboard]');
}

/**
 * A box that takes typed text. Not a dropdown: it takes no text, so Menu, Guide
 * and the rest kept doing nothing while the guide's category list had focus
 * (owner, 2026-10-10). Its arrows and OK are still its own: those buttons only
 * act here while watching full screen.
 */
function isTyping(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
  );
}

/** Hand a press back to the app as the key it was, for its built-in handling. */
function replay(key: string): void {
  const target = document.activeElement ?? document.body;
  const event = new KeyboardEvent("keydown", { key, bubbles: true, cancelable: true });
  replayed.add(event);
  target.dispatchEvent(event);
}

function perform(outcome: ButtonDefault | null, key: string): void {
  if (!outcome) return;
  if (outcome.builtIn) replay(key);
  // The viewer's choice does not apply here: the button does its own job.
  else if (!runRemoteAction(outcome.action)) replay(key);
}

interface Held {
  key: string;
  button: RemoteButton;
  timer: ReturnType<typeof setTimeout>;
  longDone: boolean;
}

export function installRemoteDispatch(): () => void {
  let held: Held | null = null;
  /** A key whose press this module took, so its repeats and key-up are taken too. */
  let swallowing: string | null = null;

  const onKeyDown = (event: KeyboardEvent) => {
    if (replayed.has(event)) return;
    const button = buttonForKey(event.key);
    if (!button) return;
    if (held && held.key === event.key) {
      // Repeats while deciding press or long press belong to that decision.
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (swallowing === event.key && event.repeat) {
      event.preventDefault();
      event.stopImmediatePropagation();
      // Held, a channel change or a seek repeats like its on-screen button;
      // anything else acts once per press.
      const again = effectiveAction(button, "press");
      if (again && !again.builtIn && REPEATS.has(again.action)) runRemoteAction(again.action);
      return;
    }
    if (isTyping(event.target) || document.querySelector("[data-tvkeyboard]")) return;
    if (button.watchingOnly && !isWatching()) return;

    const press = effectiveAction(button, "press");
    const long = effectiveAction(button, "longPress");
    const longCustom = long !== null && !long.builtIn;

    if (!longCustom) {
      // Nothing to wait for. A built-in press is left entirely alone.
      if (!press || press.builtIn) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      swallowing = event.key;
      // A choice that does not apply here — Up set to "previous channel" on a
      // film — left the press dead: it did nothing at all, not even bring up
      // the bar (onn box, 2026-10-06). The button does its own job instead.
      if (!runRemoteAction(press.action)) {
        swallowing = null;
        replay(event.key);
      }
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();
    const key = event.key;
    held = {
      key,
      button,
      longDone: false,
      timer: setTimeout(() => {
        if (!held || held.key !== key) return;
        held.longDone = true;
        runRemoteAction(long.action);
      }, LONG_PRESS_MS),
    };
  };

  const onKeyUp = (event: KeyboardEvent) => {
    if (held && held.key === event.key) {
      clearTimeout(held.timer);
      const { longDone, button } = held;
      held = null;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!longDone) perform(effectiveAction(button, "press"), event.key);
      return;
    }
    if (swallowing === event.key) {
      swallowing = null;
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  window.addEventListener("keydown", onKeyDown, true);
  window.addEventListener("keyup", onKeyUp, true);
  return () => {
    if (held) clearTimeout(held.timer);
    window.removeEventListener("keydown", onKeyDown, true);
    window.removeEventListener("keyup", onKeyUp, true);
  };
}
